import { useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import debounce from 'lodash/debounce';

import {
  AdditionalInfoConfigNumber,
  AdditionalInfoConfigSelect,
  AdditionalInfoConfigText,
  AdditionalInfoConfigTextArea,
  ArticleActionAdditionalInfo,
} from '@/api';
import InputNumber from '@/design_system/InputNumber';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import InputText from '@/design_system/InputText';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import TextArea from '@/design_system/TextArea';
import Paragraph from '@/design_system/Typography/Paragraph';
import { getAdditionalInfoConfig } from '@/models/actionType';
import { TEMPORARY_ID_PREFIX, useUpdateAction } from '@/models/article';
import { ArticleActionWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useDatabaseTranslation } from '@/services/i18n';
import { createBEMClasses } from '@/utils/classname';

import { getEditedFields } from './getEditedFields';

const { element } = createBEMClasses('actions-list');

export const ActionAdditionalInfoMobile = ({
  action,
  previousAction,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const { _db } = useDatabaseTranslation();
  const { errors } = useArticleContext();

  const actionOrPreviousAction = (action ?? previousAction)!;
  const additionalInfoConfig = getAdditionalInfoConfig(actionOrPreviousAction);

  if (!additionalInfoConfig?.length) {
    return null;
  }

  return (
    <ul
      className={element('card-additional-info', {
        edited: getEditedFields({ action, previousAction }).isAdditionalInfoEdited,
      })}
    >
      {additionalInfoConfig.map((config) => {
        let value = actionOrPreviousAction.additionalInfo?.find(
          ({ actionTypeOrganizationAdditionalInfoConfigId }) =>
            config.id === actionTypeOrganizationAdditionalInfoConfigId
        )?.value;

        if (config.type === 'select') {
          const valueOption = config.config.options.find((option) => option.id === value);

          if (valueOption) {
            value = _db(valueOption.label);
          }
        }

        const isInvalid = !!errors.services?.actions?.actions
          ?.find(({ id }) => action?.id === id)
          ?.additionalInfo?.find(({ id }) => config.id === id)?.missingValue;

        return (
          <li key={config.id}>
            <Paragraph color={isInvalid ? 'danger' : 'secondary'}>
              {_db(config.config.label)}
              <Trans id="_general.colon">:</Trans> {value ?? '-'}
            </Paragraph>
          </li>
        );
      })}
    </ul>
  );
};

export const ActionAdditionalInfoInputs = ({
  action,
  previousAction,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const { view, errors } = useArticleContext();

  const isEditable =
    !!action &&
    view.services.actions.fields.additionalInfo.editable &&
    !action.id.startsWith(TEMPORARY_ID_PREFIX);

  const actionOrPreviousAction = (action ?? previousAction)!;
  const additionalInfoConfig = (
    actionOrPreviousAction.actionTypeOrganization ??
    actionOrPreviousAction.packActionTypeOrganization
  )?.additionalInfoConfig?.filter(({ enabled }) => !!enabled);

  if (!additionalInfoConfig?.length) {
    return null;
  }

  return (
    <div
      className={element('card-additional-info', {
        edited: getEditedFields({ action, previousAction }).isAdditionalInfoEdited,
        columns: !additionalInfoConfig.find((config) => config.type === 'text-area'),
      })}
    >
      {additionalInfoConfig.map((config, index) => {
        const isInvalid = !!errors.services?.actions?.actions
          ?.find(({ id }) => action?.id === id)
          ?.additionalInfo?.find(({ id }) => config.id === id)?.missingValue;

        const commonProps = { actionOrPreviousAction, isEditable, isInvalid };

        return (
          <div
            key={config.id}
            className={element('card-additional-info__element', {
              // Last element of an odd total should be full width
              fullWidth:
                additionalInfoConfig.length % 2 === 1 && index === additionalInfoConfig.length - 1,
            })}
          >
            {config.type === 'text' && (
              <ActionAdditionalInfoTextInput config={config} {...commonProps} />
            )}
            {config.type === 'text-area' && (
              <ActionAdditionalInfoTextAreaInput config={config} {...commonProps} />
            )}
            {config.type === 'select' && (
              <ActionAdditionalInfoSelectInput config={config} {...commonProps} />
            )}
            {config.type === 'number' && (
              <ActionAdditionalInfoNumberInput config={config} {...commonProps} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const ActionAdditionalInfoTextInput = ({
  config,
  actionOrPreviousAction,
  isEditable,
  isInvalid,
}: {
  config: AdditionalInfoConfigText;
  actionOrPreviousAction: ArticleActionWithRelations;
  isEditable: boolean;
  isInvalid: boolean;
}) => {
  const { _db } = useDatabaseTranslation();
  const { request, article } = useArticleContext();

  const { mutateAsync: updateAction } = useUpdateAction({
    requestId: request.id,
    articleId: article.id,
  });

  const debouncedUpdateAction = useMemo(() => {
    return debounce(updateAction, 500);
  }, [updateAction]);

  const [value, setValue] = useState(
    actionOrPreviousAction.additionalInfo?.find(
      ({ actionTypeOrganizationAdditionalInfoConfigId }) =>
        actionTypeOrganizationAdditionalInfoConfigId === config.id
    )?.value ?? ''
  );

  return (
    <InputText
      label={
        <>
          {_db(config.config.label)}
          {!config.required && <OptionalLabel />}
        </>
      }
      labelFontWeight="regular"
      placeholder={_db(config.config.placeholder)}
      value={value}
      onChange={(newValue) => {
        setValue(newValue);
        debouncedUpdateAction({
          actionId: actionOrPreviousAction.id,
          body: {
            additionalInfo: getNewAdditionalInfo(actionOrPreviousAction.additionalInfo, {
              actionTypeOrganizationAdditionalInfoConfigId: config.id,
              value: newValue,
            }),
          },
        });
      }}
      isDisabled={!isEditable}
      isInvalid={isInvalid}
    />
  );
};

const ActionAdditionalInfoTextAreaInput = ({
  config,
  actionOrPreviousAction,
  isEditable,
  isInvalid,
}: {
  config: AdditionalInfoConfigTextArea;
  actionOrPreviousAction: ArticleActionWithRelations;
  isEditable: boolean;
  isInvalid: boolean;
}) => {
  const { _db } = useDatabaseTranslation();
  const { request, article } = useArticleContext();

  const { mutateAsync: updateAction } = useUpdateAction({
    requestId: request.id,
    articleId: article.id,
  });

  const debouncedUpdateAction = useMemo(() => {
    return debounce(updateAction, 500);
  }, [updateAction]);

  const [value, setValue] = useState(
    actionOrPreviousAction.additionalInfo?.find(
      ({ actionTypeOrganizationAdditionalInfoConfigId }) =>
        actionTypeOrganizationAdditionalInfoConfigId === config.id
    )?.value ?? ''
  );

  return (
    <TextArea
      label={
        <>
          {_db(config.config.label)}
          {!config.required && <OptionalLabel />}
        </>
      }
      labelFontWeight="regular"
      placeholder={_db(config.config.placeholder)}
      value={value}
      onChange={(event) => {
        setValue(event.target.value);
        debouncedUpdateAction({
          actionId: actionOrPreviousAction.id,
          body: {
            additionalInfo: getNewAdditionalInfo(actionOrPreviousAction.additionalInfo, {
              actionTypeOrganizationAdditionalInfoConfigId: config.id,
              value: event.target.value,
            }),
          },
        });
      }}
      disabled={!isEditable}
      isInvalid={isInvalid}
    />
  );
};

const ActionAdditionalInfoSelectInput = ({
  config,
  actionOrPreviousAction,
  isEditable,
  isInvalid,
}: {
  config: AdditionalInfoConfigSelect;
  actionOrPreviousAction: ArticleActionWithRelations;
  isEditable: boolean;
  isInvalid: boolean;
}) => {
  const { _db } = useDatabaseTranslation();
  const { request, article } = useArticleContext();

  const { mutateAsync: updateAction } = useUpdateAction({
    requestId: request.id,
    articleId: article.id,
  });

  const value = config.config.options.find(
    (option) =>
      option.id ===
      actionOrPreviousAction.additionalInfo?.find(
        ({ actionTypeOrganizationAdditionalInfoConfigId }) =>
          actionTypeOrganizationAdditionalInfoConfigId === config.id
      )?.value
  );

  return (
    <InputSelect
      variant="select"
      label={
        <>
          {_db(config.config.label)}
          {!config.required && <OptionalLabel />}
        </>
      }
      labelFontWeight="regular"
      placeholder={_db(config.config.placeholder)}
      value={value}
      options={config.config.options}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => _db(option.label)}
      onChange={(option) => {
        if (!option) {
          return;
        }

        updateAction({
          actionId: actionOrPreviousAction.id,
          body: {
            additionalInfo: getNewAdditionalInfo(actionOrPreviousAction.additionalInfo, {
              actionTypeOrganizationAdditionalInfoConfigId: config.id,
              value: option.id,
            }),
          },
        });
      }}
      isDisabled={!isEditable}
      isInvalid={isInvalid}
    />
  );
};

const ActionAdditionalInfoNumberInput = ({
  config,
  actionOrPreviousAction,
  isEditable,
  isInvalid,
}: {
  config: AdditionalInfoConfigNumber;
  actionOrPreviousAction: ArticleActionWithRelations;
  isEditable: boolean;
  isInvalid: boolean;
}) => {
  const { _db } = useDatabaseTranslation();
  const { request, article } = useArticleContext();

  const { mutateAsync: updateAction } = useUpdateAction({
    requestId: request.id,
    articleId: article.id,
  });

  const stringValue = actionOrPreviousAction.additionalInfo?.find(
    ({ actionTypeOrganizationAdditionalInfoConfigId }) =>
      actionTypeOrganizationAdditionalInfoConfigId === config.id
  )?.value;

  let value: number;

  try {
    value = stringValue ? parseFloat(stringValue) : NaN;
  } catch {
    value = NaN;
  }

  return (
    <InputNumber
      label={
        <>
          {_db(config.config.label)}
          {!config.required && <OptionalLabel />}
        </>
      }
      labelFontWeight="regular"
      placeholder="0"
      value={value}
      minValue={0}
      onChange={(newValue) => {
        updateAction({
          actionId: actionOrPreviousAction.id,
          body: {
            additionalInfo: getNewAdditionalInfo(actionOrPreviousAction.additionalInfo, {
              actionTypeOrganizationAdditionalInfoConfigId: config.id,
              value: newValue.toString(),
            }),
          },
        });
      }}
      isDisabled={!isEditable}
      isInvalid={isInvalid}
    />
  );
};

const getNewAdditionalInfo = (
  currentAdditionalInfo: ArticleActionAdditionalInfo | null,
  additionalInfoToUpdate: ArticleActionAdditionalInfo[number]
) => [
  ...(currentAdditionalInfo?.filter(
    ({ actionTypeOrganizationAdditionalInfoConfigId }) =>
      actionTypeOrganizationAdditionalInfoConfigId !==
      additionalInfoToUpdate.actionTypeOrganizationAdditionalInfoConfigId
  ) ?? []),
  additionalInfoToUpdate,
];
